import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { History as HistoryIcon, BookOpen, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { pageHead } from "@/lib/seo";

export const Route = createFileRoute("/history")({
  head: () => pageHead({ path: "/history", title: "Exchange history — Book Swap", description: "See every book request you've sent or received on Book Swap, and whether it was accepted, declined or completed.", noindex: true }),
  component: HistoryPage,
});

type Row = {
  id: string;
  status: string;
  created_at: string;
  requester_id: string;
  book_id: string;
  offered_book_id: string | null;
  message: string | null;
  books: { id: string; title: string; author: string; user_id: string; cover_url: string | null } | null;
};

type BookLite = { id: string; title: string; author: string; cover_url: string | null };

const DONE = ["accepted", "declined", "cancelled", "completed", "exchanged"];

function badgeClass(status: string) {
  if (status === "accepted" || status === "completed" || status === "exchanged")
    return "bg-primary-soft text-primary";
  if (status === "declined" || status === "cancelled") return "bg-destructive/10 text-destructive";
  return "bg-muted text-muted-foreground";
}

function statusLabel(status: string, isAr: boolean) {
  if (!isAr) return status;
  const map: Record<string, string> = {
    accepted: "مقبول",
    declined: "مرفوض",
    cancelled: "ملغى",
    completed: "مكتمل",
    exchanged: "تم التبادل",
  };
  return map[status] ?? status;
}

function HistoryPage() {
  const { user, loading } = useAuth();
  const hydrated = useHydrated();
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [incoming, setIncoming] = useState<Row[]>([]);
  const [outgoing, setOutgoing] = useState<Row[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [offered, setOffered] = useState<Record<string, BookLite>>({});
  const [tab, setTab] = useState<"incoming" | "outgoing">("incoming");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setBusy(true);
    (async () => {
      const select =
        "id,status,created_at,requester_id,book_id,offered_book_id,message,books!inner(id,title,author,user_id,cover_url)";
      const [mine, theirs] = await Promise.all([
        supabase.from("exchange_requests").select(select).eq("books.user_id", user.id).order("created_at", { ascending: false }),
        supabase.from("exchange_requests").select(select).eq("requester_id", user.id).order("created_at", { ascending: false }),
      ]);
      if (cancelled) return;
      const inRows = ((mine.data ?? []) as unknown as Row[]).filter((r) => DONE.includes(r.status) && r.requester_id !== user.id);
      const outRows = ((theirs.data ?? []) as unknown as Row[]).filter((r) => DONE.includes(r.status));
      setIncoming(inRows);
      setOutgoing(outRows);
      setBusy(false);

      const offeredIds = Array.from(
        new Set([...inRows, ...outRows].map((r) => r.offered_book_id).filter(Boolean) as string[]),
      );
      if (offeredIds.length) {
        const { data } = await supabase.from("books").select("id,title,author,cover_url").in("id", offeredIds);
        if (!cancelled) {
          const map: Record<string, BookLite> = {};
          ((data ?? []) as BookLite[]).forEach((b) => { map[b.id] = b; });
          setOffered(map);
        }
      }

      const ids = Array.from(
        new Set([...inRows.map((r) => r.requester_id), ...outRows.map((r) => r.books?.user_id ?? "")].filter(Boolean)),
      );
      if (ids.length) {
        const { data } = await supabase.rpc("get_public_profiles" as never);
        if (cancelled) return;
        const map: Record<string, string> = {};
        ((data ?? []) as { id: string; full_name: string | null }[]).forEach((p) => {
          if (ids.includes(p.id)) map[p.id] = p.full_name ?? (isAr ? "قارئ" : "A reader");
        });
        setNames(map);
      }
    })();
    return () => { cancelled = true; };
  }, [user, isAr]);

  if (!hydrated || loading)
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </div>
    );
  if (!user)
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <h1 className="text-2xl font-bold">{isAr ? "سجل التبادلات" : "Exchange history"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isAr ? "سجّل الدخول لعرض سجل طلباتك." : "Sign in to see your request history."}
          </p>
          <Link to="/login" className="mt-6 inline-block rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">
            {isAr ? "تسجيل الدخول" : "Sign in"}
          </Link>
        </div>
      </div>
    );


  const rows = tab === "incoming" ? incoming : outgoing;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10" dir={isAr ? "rtl" : "ltr"}>
        <h1 className="flex items-center gap-2 text-3xl font-bold">
          <HistoryIcon className="h-7 w-7 text-primary" /> {isAr ? "سجل التبادلات" : "Exchange history"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isAr
            ? "كل ما قبلته أو رفضته أو ألغيته أو أكملته."
            : "Everything you accepted, declined, cancelled or completed."}
        </p>
        <Link to="/exchanges" className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
          {isAr ? "شاهد الكتاب الذي تم اختياره بالمقابل ←" : "See which book was chosen in return →"}
        </Link>

        <div className="mt-6 inline-flex rounded-full bg-muted p-1">
          {(["incoming", "outgoing"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              {k === "incoming"
                ? `${isAr ? "طلبات على كتبي" : "Requests on my books"} (${incoming.length})`
                : `${isAr ? "طلباتي" : "My requests"} (${outgoing.length})`}
            </button>
          ))}
        </div>

        {busy ? (
          <div className="mt-8 flex items-center justify-center rounded-2xl bg-card p-10 shadow-[var(--shadow-card)]">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-8 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <BookOpen className="mx-auto h-10 w-10 text-primary/50" />
            <p className="mt-3 text-sm text-muted-foreground">
              {isAr ? "لا يوجد شيء هنا بعد." : "Nothing here yet."}
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {rows.map((r) => {
              const back = r.offered_book_id ? offered[r.offered_book_id] : null;
              return (
                <div key={r.id} className="flex flex-wrap items-start gap-4 rounded-2xl bg-card p-4 shadow-[var(--shadow-card)]">
                  <div className="h-20 w-14 shrink-0 overflow-hidden rounded-lg bg-primary-soft">
                    {r.books?.cover_url ? (
                      <img src={r.books.cover_url} alt={r.books.title} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center"><BookOpen className="h-5 w-5 text-primary/40" /></div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">{r.books?.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {isAr ? "بقلم" : "by"} {r.books?.author}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {tab === "incoming"
                        ? `${isAr ? "طلبه" : "Requested by"} ${names[r.requester_id] ?? (isAr ? "قارئ" : "a reader")}`
                        : `${isAr ? "المالك:" : "Owner:"} ${names[r.books?.user_id ?? ""] ?? (isAr ? "قارئ" : "a reader")}`}
                      {" · "}
                      {new Date(r.created_at).toLocaleDateString(isAr ? "ar" : "en")}
                    </p>
                    {back && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {isAr ? "الكتاب المختار بالمقابل:" : "Chosen in return:"}{" "}
                        <span className="font-medium text-foreground">{back.title}</span>
                      </p>
                    )}
                    <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ${badgeClass(r.status)}`}>
                      {statusLabel(r.status, isAr)}
                    </span>
                  </div>
                  <Link to="/requests/$id" params={{ id: r.id }}>
                    <Button size="sm" variant="outline">{isAr ? "عرض" : "View"}</Button>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
